/**
 * Task Resolver - handles long-running AI inference tasks.
 * - WebSocket: wss://.../api/v1/ws?token={access_token} for real-time task completion.
 * - One-time GET catch-up when WS connects/reconnects so we don't miss tasks that
 *   completed while disconnected (same results as before, no periodic polling).
 * - HTTP fallback: if no WS resolution within 15s, GET /api/v1/ai-inference/{taskId}
 *   periodically while the task is still pending (covers SUCCESS / FAILED the WS missed).
 *
 * Extensions beyond the original reference (task id key normalization, payload merge, token reconnect):
 * - ws URL: respect TASK_RESOLVER_CONFIG.wsBaseUrl when set (dev proxy / staging).
 * - task id: accept camelCase task ids and normalize Map keys (string vs number).
 * - messages: merge payload/data/body so nested WS shapes still resolve.
 */
import { getAiInferenceResult } from '../api/aiInference';
import { TASK_RESOLVER_CONFIG } from '../api/config';

// normalizedTaskKey -> { onResolve, onError, roomImageId? }
const taskNeedResolve = new Map();

/** Delay before first GET backup when WebSocket has not resolved the task. */
const HTTP_FALLBACK_FIRST_DELAY_MS = 15_000;
/** Interval between GET checks while the task is still in-flight. */
const HTTP_FALLBACK_INTERVAL_MS = 15_000;
/** Poll task status for UI updates while handlers.onStatusChange is registered. */
const STATUS_POLL_INTERVAL_MS = 2_000;
/** Stop polling after this many GET attempts (after the first delay) to avoid endless traffic. */
const HTTP_FALLBACK_MAX_POLLS = 24;

let ws = null;
let wsReconnectTimeout = null;
const WS_RECONNECT_DELAY = 3000;

/** taskKey -> timeout id */
const httpFallbackTimers = new Map();
/** taskKey -> timeout id for lightweight status polling */
const statusPollTimers = new Map();
/** taskKey -> number of GET attempts completed for this registration */
const httpPollAttempts = new Map();

/** Same key for add/remove/resolve whether API returns number or string task ids. */
function normalizeTaskKey(taskId) {
  if (taskId == null) return null;
  return String(taskId);
}

function getWsUrl() {
  const cfg = TASK_RESOLVER_CONFIG;
  const token = cfg.getAccessToken?.() ?? '';
  const path = cfg.wsPath || '/api/v1/ws';
  // Reference: prefer explicit wsBaseUrl (e.g. Vite proxy / env), else dev localhost, else prod default.
  const base =
    cfg.wsBaseUrl ||
    (import.meta.env.DEV && typeof window !== 'undefined'
      ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
      : 'wss://stage-api.dxinteriors.ai');
  return `${base}${path}?token=${encodeURIComponent(token)}`;
}

/** Flatten common WS envelope shapes so task id + status live on one object. */
function mergeWsPayload(msg) {
  if (!msg || typeof msg !== 'object') return msg;
  const inner = msg.payload ?? msg.data ?? msg.body;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return { ...msg, ...inner };
  }
  return msg;
}

/** One-time fetch of each pending task (on WS open) to catch completions that happened while disconnected. */
async function catchUpPendingTasks() {
  if (taskNeedResolve.size === 0) return;
  const taskIds = [...taskNeedResolve.keys()];
  await Promise.allSettled(
    taskIds.map(async (key) => {
      try {
        const data = await getAiInferenceResult(key);
        if (isTaskComplete(data)) resolveTask(key, data, 'catch-up');
      } catch {
        // leave task in map; WS may push later, or we'll catch up on next reconnect
      }
    })
  );
}

function getTaskStatus(data) {
  if (!data || typeof data !== 'object') return '';
  return String(data.status ?? data.taskStatus ?? data.state ?? '').toLowerCase();
}

function clearStatusPollTimer(taskKey) {
  const t = statusPollTimers.get(taskKey);
  if (t != null) {
    clearInterval(t);
    statusPollTimers.delete(taskKey);
  }
}

function notifyTaskStatus(taskKey, data) {
  const entry = taskNeedResolve.get(taskKey);
  if (!entry?.onStatusChange) return;
  const status = getTaskStatus(data);
  if (status) entry.onStatusChange(status, data);
}

function scheduleStatusPolling(taskKey) {
  const entry = taskNeedResolve.get(taskKey);
  if (!entry?.onStatusChange) return;
  clearStatusPollTimer(taskKey);
  const poll = async () => {
    if (!taskNeedResolve.has(taskKey)) {
      clearStatusPollTimer(taskKey);
      return;
    }
    try {
      const data = await getAiInferenceResult(taskKey);
      if (!taskNeedResolve.has(taskKey)) return;
      if (isTaskComplete(data)) {
        clearStatusPollTimer(taskKey);
        resolveTask(taskKey, data, 'http-poll');
        return;
      }
      notifyTaskStatus(taskKey, data);
    } catch (err) {
      console.warn('[Task resolver] status poll GET failed:', taskKey, err);
    }
  };
  void poll();
  const id = setInterval(() => {
    void poll();
  }, STATUS_POLL_INTERVAL_MS);
  statusPollTimers.set(taskKey, id);
}

function clearHttpFallbackTimer(taskKey) {
  const t = httpFallbackTimers.get(taskKey);
  if (t != null) {
    clearTimeout(t);
    httpFallbackTimers.delete(taskKey);
  }
}

function scheduleHttpFallbackPoll(taskKey, delayMs) {
  clearHttpFallbackTimer(taskKey);
  const id = setTimeout(() => {
    void pollPendingTaskViaHttp(taskKey);
  }, delayMs);
  httpFallbackTimers.set(taskKey, id);
}

/**
 * GET /ai-inference/{task_id} while task is still registered — same payload shape as WS completion.
 */
async function pollPendingTaskViaHttp(taskKey) {
  if (!taskNeedResolve.has(taskKey)) {
    clearHttpFallbackTimer(taskKey);
    httpPollAttempts.delete(taskKey);
    return;
  }
  const nextAttempt = (httpPollAttempts.get(taskKey) ?? 0) + 1;
  if (nextAttempt > HTTP_FALLBACK_MAX_POLLS) {
    clearHttpFallbackTimer(taskKey);
    httpPollAttempts.delete(taskKey);
    console.warn('[Task resolver] HTTP fallback polling stopped (max attempts):', taskKey);
    return;
  }
  httpPollAttempts.set(taskKey, nextAttempt);

  try {
    const data = await getAiInferenceResult(taskKey);
    if (!taskNeedResolve.has(taskKey)) return;
    if (isTaskComplete(data)) {
      clearHttpFallbackTimer(taskKey);
      resolveTask(taskKey, data, 'http-poll');
      return;
    }
    notifyTaskStatus(taskKey, data);
  } catch (err) {
    console.warn('[Task resolver] HTTP fallback GET failed:', taskKey, err);
  }

  if (!taskNeedResolve.has(taskKey)) {
    clearHttpFallbackTimer(taskKey);
    httpPollAttempts.delete(taskKey);
    return;
  }
  scheduleHttpFallbackPoll(taskKey, HTTP_FALLBACK_INTERVAL_MS);
}

function isTaskComplete(data) {
  if (!data) return false;
  const s = String(data.status || '').toLowerCase();
  if (s === 'pending' || s === 'processing' || s === 'started' || s === 'running' || s === 'queued') {
    return false;
  }
  if (data.polygons != null || data.taskResult != null) return true;
  if (s === 'completed' || s === 'done' || s === 'success') return true;
  if (s === 'failed' || s === 'error') return true;
  if (s === 'cancelled' || s === 'canceled') return true;
  return false;
}

/** Backend may send snake_case or camelCase result keys; expose both on taskResult. */
function normalizeTaskResultPayload(tr) {
  if (tr == null) return tr;
  if (typeof tr !== 'object' || Array.isArray(tr)) return tr;

  const imageKey = tr.resultS3Key ?? tr.result_s3_key;
  const videoKey = tr.resultVideoS3Key ?? tr.result_video_s3_key;
  const thumbnailKey = tr.thumbnailS3Key ?? tr.thumbnail_s3_key;

  const normalized = { ...tr };
  if (imageKey != null) {
    normalized.resultS3Key = imageKey;
    normalized.result_s3_key = tr.result_s3_key ?? tr.resultS3Key ?? imageKey;
  }
  if (videoKey != null) {
    normalized.resultVideoS3Key = videoKey;
    normalized.result_video_s3_key = tr.result_video_s3_key ?? tr.resultVideoS3Key ?? videoKey;
  }
  if (thumbnailKey != null) {
    normalized.thumbnailS3Key = thumbnailKey;
    normalized.thumbnail_s3_key = tr.thumbnail_s3_key ?? tr.thumbnailS3Key ?? thumbnailKey;
  }
  return normalized;
}

/**
 * @param {'ws' | 'catch-up' | 'http-poll'} resolution
 */
function resolveTask(taskKey, data, resolution = 'ws') {
  const entry = taskNeedResolve.get(taskKey);
  if (!entry) return;
  clearHttpFallbackTimer(taskKey);
  clearStatusPollTimer(taskKey);
  httpPollAttempts.delete(taskKey);
  taskNeedResolve.delete(taskKey);

  const s = data ? String(data.status || '').toLowerCase() : '';
  const hasResult =
    data && (data.polygons != null || data.taskResult != null);
  const isCancelled = s === 'cancelled' || s === 'canceled';
  const isSuccess = data && (hasResult || s === 'completed' || s === 'done' || s === 'success');

  if (isCancelled) {
    const err = new Error(data?.message || data?.error || 'Task cancelled');
    err.cancelled = true;
    entry.onError?.(err);
    return;
  }

  if (isSuccess) {
    const rawTr = data.taskResult ?? data.polygons;
    const normalized = {
      ...data,
      // Normalize backend field name so callers can always use taskResult
      taskResult: normalizeTaskResultPayload(rawTr) ?? rawTr,
    };
    const via =
      resolution === 'ws'
        ? 'WebSocket'
        : resolution === 'http-poll'
          ? 'HTTP poll fallback'
          : 'catch-up GET';
    console.log(`Task ${taskKey} resolved (via ${via}):`, normalized);
    entry.onResolve?.(normalized);
  } else {
    entry.onError?.(new Error(data?.message || data?.error || 'Task failed'));
  }
}

function ensureWsConnected() {
  const cfg = TASK_RESOLVER_CONFIG;
  if (!cfg.wsEnabled || taskNeedResolve.size === 0) return;

  if (ws?.readyState === WebSocket.OPEN) return;

  const url = getWsUrl();
  console.log('[Task resolver] WebSocket connecting...');
  try {
    ws = new WebSocket(url);
    ws.onopen = () => {
      console.log('[Task resolver] WebSocket connected');
      catchUpPendingTasks();
    };
    ws.onmessage = (event) => {
      try {
        const raw = event.data;
        //console.log('[Task resolver] WebSocket message raw:', raw);
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const msg = mergeWsPayload(parsed);
        console.log('[Task resolver] WebSocket message parsed:', msg);
        // Backend sends camelCase task identifiers; POST /ai-inference returns numeric id.
        const rawTaskId =
          msg.taskId ??
          msg.aiTaskId ??
          msg.id;
        if (rawTaskId == null) {
          console.warn('[Task resolver] WebSocket message without task id, ignoring.', msg);
          return;
        }
        const taskKey = normalizeTaskKey(rawTaskId);
        if (!taskNeedResolve.has(taskKey)) {
          const status = String(msg?.status || '').toLowerCase();
          if (status === 'success' || status === 'completed' || status === 'done') {
            console.debug('[Task resolver] WebSocket success for non-pending taskId (likely duplicate/late event):', taskKey, msg);
          } else {
            console.warn('[Task resolver] WebSocket message for unknown taskId:', taskKey, msg);
          }
          return;
        }
        if (!isTaskComplete(msg)) {
          notifyTaskStatus(taskKey, msg);
          return;
        }
        resolveTask(taskKey, msg, 'ws');
      } catch (err) {
        console.warn('[Task resolver] Failed to handle WebSocket message:', err);
      }
    };
    ws.onclose = () => {
      console.log('[Task resolver] WebSocket disconnected');
      ws = null;
      if (taskNeedResolve.size > 0) {
        wsReconnectTimeout = setTimeout(ensureWsConnected, WS_RECONNECT_DELAY);
      }
    };
    ws.onerror = () => {
      // onclose will fire; rely on reconnect
    };
  } catch (err) {
    console.warn('Task resolver: WebSocket connect failed:', err);
    wsReconnectTimeout = setTimeout(ensureWsConnected, WS_RECONNECT_DELAY);
  }
}

/**
 * Register a task to be resolved. Primary path: WebSocket; catch-up GET when WS opens;
 * after 15s without resolution, GET /ai-inference/{taskId} on an interval until complete or max polls.
 * @param {number|string} taskId
 * @param {{ onResolve: (data) => void, onError: (err) => void, roomImageId?: number }} handlers
 */
export function addTask(taskId, handlers) {
  const key = normalizeTaskKey(taskId);
  if (key == null) {
    console.warn('[Task resolver] addTask called with null/undefined taskId');
    return;
  }
  clearHttpFallbackTimer(key);
  clearStatusPollTimer(key);
  httpPollAttempts.delete(key);
  taskNeedResolve.set(key, { ...handlers });
  if (handlers.onStatusChange) {
    scheduleStatusPolling(key);
  }
  scheduleHttpFallbackPoll(key, HTTP_FALLBACK_FIRST_DELAY_MS);
  ensureWsConnected();
}

/**
 * Remove a task from the pending set (e.g. user navigated away).
 */
export function removeTask(taskId) {
  const key = normalizeTaskKey(taskId);
  if (key != null) {
    clearHttpFallbackTimer(key);
    clearStatusPollTimer(key);
    httpPollAttempts.delete(key);
    taskNeedResolve.delete(key);
  }
  if (taskNeedResolve.size === 0 && wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
}

/**
 * Cancel a registered task: reject its waiter and stop polling.
 * @param {number|string} taskId
 * @param {string} [message]
 */
export function cancelTask(taskId, message = 'Task cancelled') {
  const key = normalizeTaskKey(taskId);
  if (key == null) return;

  const entry = taskNeedResolve.get(key);
  clearHttpFallbackTimer(key);
  clearStatusPollTimer(key);
  httpPollAttempts.delete(key);
  taskNeedResolve.delete(key);

  if (entry?.onError) {
    const err = new Error(message);
    err.cancelled = true;
    entry.onError(err);
  }

  if (taskNeedResolve.size === 0 && wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
}

/**
 * Configure the task resolver (call from AuthProvider).
 * @param {{ getAccessToken: () => string | null }}
 */
export function configureTaskResolver({ getAccessToken }) {
  TASK_RESOLVER_CONFIG.getAccessToken = getAccessToken ?? (() => null);
}

/**
 * Clear all pending tasks (e.g. on logout).
 */
export function clearAllTasks() {
  for (const k of httpFallbackTimers.keys()) {
    clearHttpFallbackTimer(k);
  }
  for (const k of statusPollTimers.keys()) {
    clearStatusPollTimer(k);
  }
  httpPollAttempts.clear();
  taskNeedResolve.clear();
  if (ws) {
    ws.close();
    ws = null;
  }
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
}

/**
 * Call after access token changes (e.g. refresh) so the next WebSocket uses
 * `?token=` from configureTaskResolver. Closes any existing socket; reconnects
 * if there are still pending tasks.
 */
export function reconnectTaskResolverAfterTokenChange() {
  if (ws) {
    try {
      ws.close();
    } catch {
      /* ignore */
    }
    ws = null;
  }
  if (wsReconnectTimeout) {
    clearTimeout(wsReconnectTimeout);
    wsReconnectTimeout = null;
  }
  if (taskNeedResolve.size > 0) {
    ensureWsConnected();
  }
}
